import UnarchiveIcon from '@mdi/svg/svg/archive-arrow-up.svg?react';
import ArchiveIcon from '@mdi/svg/svg/archive.svg?react';
import BugIcon from '@mdi/svg/svg/bug.svg?react';
import CloudUploadIcon from '@mdi/svg/svg/cloud-upload.svg?react';
import JSONIcon from '@mdi/svg/svg/code-json.svg?react';
import CopyIcon from '@mdi/svg/svg/content-copy.svg?react';
import DatabasePlusIcon from '@mdi/svg/svg/database-plus.svg?react';
import DeleteIcon from '@mdi/svg/svg/delete.svg?react';
import DotsVertical from '@mdi/svg/svg/dots-vertical.svg?react';
import FolderSearchIcon from '@mdi/svg/svg/folder-search.svg?react';
import LinkVariantIcon from '@mdi/svg/svg/link-variant.svg?react';
import ViewShareIcon from '@mdi/svg/svg/open-in-new.svg?react';
import PencilIcon from '@mdi/svg/svg/pencil.svg?react';
import StarIcon from '@mdi/svg/svg/star.svg?react';
import WebIcon from '@mdi/svg/svg/web.svg?react';
import { useUserHasFeature, useUserPreferences, useUserSession } from '@solinkcorp/authentication';
import { ButtonMenu, MDIIcon, useModal } from '@solinkcorp/solink-components';
import PrintIcon from '@solinkcorp/solink-components/svg/print.svg?react';
import { AddToDatasetSheet } from 'components/AddToDatasetSheet/AddToDatasetSheet';
import AIVerificationDebugModal from 'components/AIVerificationDebugModal';
import ArchivePanel from 'components/ArchivePanel/ArchivePanel';
import { CommonContext } from 'components/Contexts';
import { AddToInvestigationModal } from 'components/InvestigationsPage/AddToInvestigationModal';
import JSONModal from 'components/JSONModal';
import PrintPanel from 'components/PrintPanel/PrintPanel';
import SavePanel from 'components/SavePanel/SavePanel';
import EditScheduledSpotCheckWrapper from 'components/ScheduledActivityForm/EditScheduledSpotCheckWrapper';
import { useContext, useState, type ReactNode } from 'react';
import { isKiosk } from 'src/core/utils/kioskUtils';
import { useCurrentFeature } from 'src/hooks/useEventFeature';
import useFluxStore from 'src/hooks/useFluxStore';
import useLocale from 'src/hooks/useLocale';
import { LocationStore } from 'stores/LocationStore';
import * as EventUtil from 'utils/EventUtil';
import FeaturesUtil from 'utils/FeaturesUtil';
import { ARCHIVED_FEATURE } from 'vd-constants';
import { type EventMenuActionsProps as BaseEventMenuActionsProps } from './useEventMenuActions';

interface EventActionsMenuProps extends BaseEventMenuActionsProps {
  colorMode?: 'dark' | 'light';
  favourite?: boolean;
  toggleEventFavouriteMenu: () => void;
  eventDescription: string;
  enterEditMode: () => void;
  isAlarms?: boolean;
  isClips?: boolean;
  /**
   * When set, the card is already inside this investigation: the add action
   * becomes "Add to more investigations" and a remove action is shown.
   */
  investigationId?: string;
  onRemoveFromInvestigation?: () => void;
}

interface EventActionMenuItem {
  className?: string | false;
  focusOnOpen?: boolean;
  icon?: ReactNode;
  isVisible?: boolean | '' | 0;
  onClick?: () => void;
  text: string;
  type: 'action';
  attributes?: Record<string, string>;
  destructive?: boolean;
}
export default function EventMenuActions(props: EventActionsMenuProps) {
  const {
    colorMode,
    event,
    savePanelProps,
    archivePanelProps,
    printPanelProps,
    scheduledEventFormProps,
    handleOpenSavePanel,
    handleOpenArchivePanel,
    handleOpenPrintPanel,
    handleCopyShareLink,
    handleContextine,
    handleCopyLink,
    getShareUrl,
    viewShare,
    updateEvent,
    playingCameras,
    removeEvent,
    favourite,
    toggleEventFavouriteMenu,
    eventDescription,
    enterEditMode,
    scheduledActivity,
    invalidateScheduledActivity,
    additionalArchiveActions,
    additionalUnarchiveActions,
    isSaveClipMenuVisible,
    isAlarms,
    isClips,
    investigationId,
    onRemoveFromInvestigation,
  } = props;

  const isFavourited = favourite ?? !!event.details?.favourite;

  const location = useFluxStore(
    LocationStore,
    (store) => event?.siteId && store.getLocation(event.siteId),
  );

  const locale = useLocale();

  const {
    authData: { contextineEnabled },
  } = useContext(CommonContext);

  const { isSolinkUser } = useUserSession();

  const feature = useCurrentFeature();

  const [preferences] = useUserPreferences();

  const isEditAllowed = !EventUtil.isEventIdInvalid(event.id);

  const favouriteText = !isFavourited
    ? locale.event.actionFavourite
    : locale.event.actionUnfavourite;

  const isViewSavedMenuVisible = EventUtil.canViewSavedEvent(event);

  const isCopyShareLinkVisible = EventUtil.canViewShareLink(event);

  const userHasPermissionToArchive = EventUtil.userHasPermissionToArchiveEvent(event, isSolinkUser);

  const userHasPermissionToUnarchive = FeaturesUtil.isImpersonating();

  const archivedTabIsSelected = feature === ARCHIVED_FEATURE;

  const isArchiveEventActionVisible = !archivedTabIsSelected && userHasPermissionToArchive;

  const isUnarchiveEventActionVisible = archivedTabIsSelected && userHasPermissionToUnarchive;

  const editNoteText =
    eventDescription.length > 0 ? locale.event.actionEditNotes : locale.event.actionAddNotes;

  const aiDatasetsEnabled = useUserHasFeature('aiDatasetsEnabled');
  const investigationsEnabled = useUserHasFeature('investigations');
  const [addToDatasetOpen, setAddToDatasetOpen] = useState(false);
  const [addToInvestigationOpen, setAddToInvestigationOpen] = useState(false);

  const eventKind: 'event' | 'clip' = isClips ? 'clip' : 'event';
  const inInvestigation = Boolean(investigationId);

  const jsonModal = useModal();
  const aiVerificationDebugModal = useModal();
  const addToInvestigationText = inInvestigation
    ? locale.investigations.addToMoreInvestigations
    : locale.investigations.addToInvestigation;

  const hasValidVideoLinkItems = (event.videoLinkItems || []).some(
    (item) => !!item.metadata?.transactionId,
  );

  const showAiVerificationMenuItem = !!(isAlarms && isSolinkUser && hasValidVideoLinkItems);

  const menuItems: EventActionMenuItem[] = [
    {
      className: 'save-clip',
      focusOnOpen: isSaveClipMenuVisible,
      icon: <MDIIcon icon={CloudUploadIcon} size={18} />,
      isVisible: isSaveClipMenuVisible,
      onClick: () => handleOpenSavePanel({ saveMode: true, saveExistingEvent: true }),
      text: locale.videoPlayerOptions.saveClip,
      type: 'action',
    },
    {
      className: 'view-saved',
      focusOnOpen: !isSaveClipMenuVisible && isViewSavedMenuVisible,
      icon: <MDIIcon icon={CloudUploadIcon} size={18} />,
      isVisible: isViewSavedMenuVisible,
      onClick: () => handleOpenSavePanel({ saveMode: false, saveExistingEvent: true }),
      text: locale.event.actionViewSaved,
      type: 'action',
    },
    {
      className: 'copy-share-link',
      icon: <MDIIcon icon={CopyIcon} size={18} />,
      isVisible: isCopyShareLinkVisible,
      text: locale.event.actionCopyShareLink,
      onClick: handleCopyShareLink,
      type: 'action',
    },
    {
      className: 'view-share',
      icon: <MDIIcon icon={ViewShareIcon} size={18} />,
      isVisible: isCopyShareLinkVisible,
      text: locale.event.actionViewShare,
      onClick: viewShare,
      type: 'action',
    },
    {
      className: 'edit-description',
      focusOnOpen: !isSaveClipMenuVisible && !isViewSavedMenuVisible,
      icon: <MDIIcon icon={PencilIcon} size={18} />,
      isVisible: isEditAllowed,
      onClick: enterEditMode,
      text: editNoteText,
      type: 'action',
    },
    {
      className: 'toggle-event-favourite',
      icon: <MDIIcon icon={StarIcon} size={18} />,
      isVisible: isEditAllowed,
      onClick: toggleEventFavouriteMenu,
      text: favouriteText,
      type: 'action',
    },
    {
      attributes: {
        'data-attr': getShareUrl(),
      },
      className: 'copy-link',
      icon: <MDIIcon icon={LinkVariantIcon} size={18} />,
      isVisible: isEditAllowed && !isKiosk(),
      onClick: handleCopyLink,
      text: locale.event.actionCopyLink,
      type: 'action',
    },
    {
      className: 'share-to-contextine',
      icon: <MDIIcon icon={WebIcon} size={18} />,
      isVisible: contextineEnabled,
      onClick: handleContextine,
      text: locale.share.shareToContextine,
      type: 'action',
    },
    {
      className: 'print',
      onClick: handleOpenPrintPanel,
      icon: <MDIIcon icon={PrintIcon} size={18} />,
      text: locale.print.printAction,
      type: 'action',
    },
    {
      className: 'add-to-dataset',
      icon: <MDIIcon icon={DatabasePlusIcon} size={18} />,
      isVisible: aiDatasetsEnabled,
      onClick: () => setAddToDatasetOpen(true),
      text: 'Add to Dataset',
      type: 'action',
    },
    {
      className: 'add-to-investigation',
      icon: <MDIIcon icon={FolderSearchIcon} size={18} />,
      isVisible: investigationsEnabled && !!event.siteId,
      onClick: () => setAddToInvestigationOpen(true),
      text: addToInvestigationText,
      type: 'action',
    },
    {
      text: 'View Data',
      icon: <MDIIcon icon={JSONIcon} size={18} />,
      onClick: jsonModal.openModal,
      className: 'internal-highlight-archive',
      type: 'action',
      isVisible: isSolinkUser,
    },
    {
      text: 'AI Verification Debug',
      icon: <MDIIcon icon={BugIcon} size={18} />,
      onClick: aiVerificationDebugModal.openModal,
      className: 'ai-verification-debug',
      type: 'action',
      isVisible: showAiVerificationMenuItem,
    },
    {
      onClick: () => handleOpenArchivePanel({ type: 'archive' }),
      icon: <MDIIcon icon={ArchiveIcon} size={18} />,
      isVisible: isArchiveEventActionVisible,
      text: locale.archive.archiveAction,
      type: 'action',
      className:
        isSolinkUser && !EventUtil.eventIsUserCreated(event) && 'internal-highlight-archive',
    },
    {
      onClick: () => handleOpenArchivePanel({ type: 'unarchive' }),
      icon: <MDIIcon icon={UnarchiveIcon} size={18} />,
      isVisible: isUnarchiveEventActionVisible,
      text: locale.archive.unarchiveAction,
      type: 'action',
    },
    {
      className: 'remove-from-investigation',
      icon: <MDIIcon icon={DeleteIcon} size={18} />,
      isVisible: inInvestigation && !!onRemoveFromInvestigation,
      onClick: onRemoveFromInvestigation,
      text: locale.investigations.removeFromInvestigation,
      type: 'action',
      destructive: true,
    },
  ];

  return (
    <>
      <ButtonMenu
        buttonIcon={<DotsVertical width={19} height={19} />}
        buttonTooltip={locale.general.options}
        buttonIconOnly={true}
        buttonType="clear"
        className="event-actions-menu"
        colorMode={colorMode}
        dataTestId="events-menu"
        menuClass="event-actions-popup-menu"
        menuItems={menuItems}
        tooltipVerticalOffset={5}
      />

      {savePanelProps && (
        <SavePanel
          updateEvent={updateEvent}
          {...savePanelProps}
          savePanelOpen
          playingCameras={playingCameras}
          preferences={preferences}
        />
      )}
      {printPanelProps && <PrintPanel {...printPanelProps} printPanelOpen />}
      {archivePanelProps && (
        <ArchivePanel
          {...archivePanelProps}
          archivePanelOpen
          additionalUnarchiveActions={additionalUnarchiveActions}
          additionalArchiveActions={additionalArchiveActions}
          removeEvent={removeEvent}
        />
      )}
      {scheduledEventFormProps && scheduledActivity && EventUtil.isScheduledAIChatEvent(event) && (
        <EditScheduledSpotCheckWrapper
          scheduledEventFormProps={scheduledEventFormProps}
          scheduledActivity={scheduledActivity}
          timezoneId={location?.timezone}
          invalidateScheduledActivity={invalidateScheduledActivity}
          source="eventsPage"
          analyticsSource="eventCard"
        />
      )}
      <JSONModal src={event} {...jsonModal} title="Event" timezone={location?.timezone} />
      <AIVerificationDebugModal event={event} {...aiVerificationDebugModal} />
      {aiDatasetsEnabled && (
        <AddToDatasetSheet
          open={addToDatasetOpen}
          onOpenChange={setAddToDatasetOpen}
          eventId={event.id}
        />
      )}
      {investigationsEnabled && event.siteId && (
        <AddToInvestigationModal
          open={addToInvestigationOpen}
          onOpenChange={setAddToInvestigationOpen}
          eventId={event.id}
          siteId={event.siteId}
          kind={eventKind}
        />
      )}
    </>
  );
}
